<template>
  <div class="toast-container">
    <transition-group name="toast-fade" tag="div">
      <div v-for="toast in toasts" :key="toast.id" class="toast-item">
        {{ toast.message }}
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, Ref, ref } from 'vue'
import emitter from '../../services/emitter'

interface ToastMessage {
  id: number
  message: string
}

const DURATION_MS = 2500
const toasts: Ref<ToastMessage[]> = ref([])
let counter = 0

onMounted(() => {
  emitter.on('toast', showToast)
})

onBeforeUnmount(() => {
  emitter.off('toast', showToast)
})

// Shows a transient message at the bottom of the screen. Multiple rapid calls stack;
// each auto-dismisses independently after its duration (DURATION_MS by default).
function showToast(payload: { message: string, duration?: number }) {
  if (!payload?.message) {
    return
  }
  const id = ++counter
  toasts.value.push({ id, message: payload.message })
  setTimeout(() => remove(id), payload.duration ?? DURATION_MS)
}

function remove(id: number) {
  toasts.value = toasts.value.filter(toast => toast.id !== id)
}
</script>

<style lang="scss" scoped>
.toast-container {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: max-content;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}

.toast-item {
  background-color: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  text-align: center;
  overflow-wrap: anywhere;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
